import React from 'react';
import styled from 'styled-components';
import {
  columns,
  columnsWithNumbers,
  oneSizeColumns,
} from '~/common/constants/sizing';
import { Table } from '~/components/table';
import { TableEntity } from '~/types/sizings';
import gsap from 'gsap';
import { colors } from '~/theme/colors';

interface ISizeTableProps {
  data?: TableEntity;
}

const convertCmToInch = (data: (string | number)[][]) => {
  return data.map((row) => {
    return row.map((cell) => {
      if (typeof cell === 'string') return cell.replace('CM', 'IN');
      return ((cell as number) / 2.54).toFixed(2);
    });
  });
};

const SizeTable = ({ data }: ISizeTableProps) => {
  const [metric, setMetric] = React.useState<'cm' | 'inch'>('cm');
  const toggleRef = React.useRef(null);
  if (!data) return null;

  const changeMetric = (newMetric: 'cm' | 'inch') => {
    setMetric(newMetric);
    gsap.to(toggleRef.current, {
      x: newMetric === 'cm' ? 0 : '98%',
      duration: 0.05,
      ease: 'power4.inOut',
    });
  };

  return (
    <Wrapper>
      <Table
        rows={metric === 'cm' ? data.sizing : convertCmToInch(data.sizing)}
        keyExtension={data.title}
        columns={
          data.sizing[0].length === 2
            ? oneSizeColumns
            : data.title === 'denim jeans' || data.title === 'wide leg jeans'
            ? columnsWithNumbers
            : columns
        }
      />
      <ToggleContainer>
        <Slider ref={toggleRef} />
        <ToggleButton
          active={metric === 'cm'}
          id="cm"
          onClick={() => changeMetric('cm')}
        >
          <p>METRIC</p>
        </ToggleButton>

        <ToggleButton
          active={metric === 'inch'}
          id="in"
          onClick={() => changeMetric('inch')}
        >
          <p>IMPERIAL</p>
        </ToggleButton>
      </ToggleContainer>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const ToggleContainer = styled.div`
  display: flex;
  position: relative;
  border: 0.0625rem solid ${({ theme }) => theme.colors.text};
  border-radius: 0.875rem;
  height: 2.5rem;
  padding: 0.1rem;
  width: 9.9375rem;
  margin-left: 2rem;

  @media ${({ theme }) => theme.breakpoints.md} {
    margin-left: 1rem;
    margin-right: 1rem;
  }
`;

const ToggleButton = styled.div<{ active?: boolean }>`
  width: 50%;
  height: 100%;
  display: flex;
  align-items: center;
  text-align: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  -webkit-tap-highlight-color: ${colors.transparent};
  p {
    font-size: 0.75rem;
    transition: all 0.5s ease-in-out;
    font-size: 0.75rem;
    color: ${({ active }) => (active ? colors.white : colors.text)};
    &:hover {
      color: ${({ theme, active }) => !active && theme.colors.textHover};
    }

    @media ${({ theme }) => theme.breakpoints.md} {
      font-size: 0.625rem;
    }
  }
`;

const Slider = styled.div`
  position: absolute;
  top: 0.0625rem;
  left: 0.0625rem;
  right: 0;
  width: 50%;
  height: calc(100% - 0.125rem);
  z-index: 1;
  background-color: ${colors.primaryDark};
  color: ${colors.white};
  border-radius: 0.75rem;
  transition: all 0.5s ease-in-out;
`;

export default SizeTable;
